/**
 * PLANTILLAS DE FESTIVOS — FASE 6.3 §E
 *
 * Catálogo global (sin centro) sobre `plantillas_calendario`/
 * `plantilla_calendario_dias`. "Aplicar" vive en la pantalla del centro
 * (`componentes/festivos-centro.tsx`) — aquí sólo se gestiona el
 * contenido de la plantilla: crearla, nombrarla, y marcar/desmarcar sus
 * días con el mismo calendario anual que usa un centro.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { api, FalloApi, type DiaPlantilla, type PlantillaCalendario } from '../api.ts';
import { CalendarioAnual, type DiaCalendario } from '../componentes/calendario-anual.tsx';
import { CampoTexto, Error as ErrorCaja, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';

export function PaginaPlantillasCalendario(): ReactNode {
  const { puede } = useSesion();
  const puedeEditar = puede('calendario:editar');
  const plantillas = useDatos(() => api.get<{ plantillas: PlantillaCalendario[] }>('/api/plantillas-calendario'), []);
  const [creando, setCreando] = useState(false);
  const [abierta, setAbierta] = useState<string | null>(null);

  return (
    <Shell
      titulo="Plantillas de festivos"
      migas={[{ texto: 'Plantillas de festivos' }]}
      acciones={
        puedeEditar ? (
          <button type="button" className="principal" onClick={() => setCreando((v) => !v)}>
            {creando ? 'Cancelar' : 'Nueva plantilla'}
          </button>
        ) : undefined
      }
    >
      {creando && (
        <FormNuevaPlantilla
          alCrear={() => {
            setCreando(false);
            plantillas.recargar();
          }}
          alCancelar={() => setCreando(false)}
        />
      )}

      <div className="panel">
        <div className="panel-cuerpo sin-relleno">
          <Panel estado={plantillas}>
            {(p) =>
              p.plantillas.length === 0 ? (
                <Vacio titulo="Sin plantillas de festivos">
                  <p className="suave">Nombre libre — por ejemplo «Castilla-La Mancha 2027» o «Motilla del Palancar 2027».</p>
                </Vacio>
              ) : (
                <div className="tabla-desliza">
                  <table>
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Nombre</th>
                        <th>Días</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {p.plantillas.map((pl) => (
                        <FilaPlantilla
                          key={pl.id}
                          plantilla={pl}
                          abierta={abierta === pl.id}
                          puedeEditar={puedeEditar}
                          onAbrir={() => setAbierta((a) => (a === pl.id ? null : pl.id))}
                          onBorrada={plantillas.recargar}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            }
          </Panel>
        </div>
      </div>

      {abierta !== null && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cuerpo">
            <CalendarioPlantilla
              idPlantilla={abierta}
              puedeEditar={puedeEditar}
              onCambio={plantillas.recargar}
            />
          </div>
        </div>
      )}
    </Shell>
  );
}

function FilaPlantilla({
  plantilla,
  abierta,
  puedeEditar,
  onAbrir,
  onBorrada,
}: {
  plantilla: PlantillaCalendario;
  abierta: boolean;
  puedeEditar: boolean;
  onAbrir: () => void;
  onBorrada: () => void;
}): ReactNode {
  const [confirmando, setConfirmando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function borrar(): Promise<void> {
    setBorrando(true);
    setError(null);
    try {
      await api.del(`/api/plantillas-calendario/${plantilla.id}`);
      onBorrada();
    } catch (err) {
      setError(err);
      setBorrando(false);
    }
  }

  return (
    <tr>
      <td className="mono">{plantilla.codigo}</td>
      <td>{plantilla.nombre}</td>
      <td>{plantilla.dias}</td>
      <td className="derecha">
        {error !== null && <span className="pequena tenue">Error al eliminar. </span>}
        <button type="button" className="pequeno" onClick={onAbrir}>
          {abierta ? 'Cerrar calendario' : 'Editar calendario'}
        </button>
        {puedeEditar &&
          (confirmando ? (
            <>
              <button type="button" className="pequeno" onClick={() => { void borrar(); }} disabled={borrando} style={{ marginLeft: 6 }}>
                {borrando ? '…' : 'Confirmar'}
              </button>
              <button type="button" className="pequeno" onClick={() => setConfirmando(false)} disabled={borrando} style={{ marginLeft: 6 }}>
                Cancelar
              </button>
            </>
          ) : (
            <button type="button" className="pequeno" onClick={() => setConfirmando(true)} style={{ marginLeft: 6 }}>
              Eliminar
            </button>
          ))}
      </td>
    </tr>
  );
}

function FormNuevaPlantilla({ alCrear, alCancelar }: { alCrear: () => void; alCancelar: () => void }): ReactNode {
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post<{ id: string }>('/api/plantillas-calendario', {
        codigo,
        nombre,
        descripcion: descripcion.trim() || null,
      });
      alCrear();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="panel" onSubmit={(e) => { void enviar(e); }} style={{ marginBottom: 16 }}>
      <div className="panel-cabecera">
        <h2>Nueva plantilla</h2>
      </div>
      <div className="panel-cuerpo">
        <div className="form-grupo">
          <CampoTexto label="Código" value={codigo} onChange={setCodigo} error={camposMal['codigo']} disabled={enviando} required />
          <CampoTexto label="Nombre" value={nombre} onChange={setNombre} error={camposMal['nombre']} disabled={enviando} required />
        </div>
        <CampoTexto label="Descripción (opcional)" value={descripcion} onChange={setDescripcion} error={camposMal['descripcion']} disabled={enviando} />
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila-botones">
          <button type="submit" className="principal" disabled={enviando}>
            {enviando ? 'Creando…' : 'Crear plantilla'}
          </button>
          <button type="button" onClick={alCancelar} disabled={enviando}>
            Cancelar
          </button>
        </div>
      </div>
    </form>
  );
}

// ------------------------------------------------------------- su calendario

function CalendarioPlantilla({
  idPlantilla,
  puedeEditar,
  onCambio,
}: {
  idPlantilla: string;
  puedeEditar: boolean;
  onCambio: () => void;
}): ReactNode {
  const [anio, setAnio] = useState(() => new Date().getFullYear());
  const dias = useDatos(() => api.get<{ dias: DiaPlantilla[] }>(`/api/plantillas-calendario/${idPlantilla}/dias`), [idPlantilla]);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);

  return (
    <div>
      <div className="fila separa" style={{ marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>Calendario de la plantilla</h3>
        <div className="fila">
          <button type="button" className="pequeno" onClick={() => setAnio((a) => a - 1)} aria-label="Año anterior">
            ◀ {anio - 1}
          </button>
          <strong>{anio}</strong>
          <button type="button" className="pequeno" onClick={() => setAnio((a) => a + 1)} aria-label="Año siguiente">
            {anio + 1} ▶
          </button>
        </div>
      </div>
      <Panel estado={dias}>
        {(d) => {
          const mapa = new Map<string, DiaCalendario>();
          for (const dia of d.dias) {
            if (!dia.fecha.startsWith(String(anio))) continue;
            mapa.set(dia.fecha, { fecha: dia.fecha, descripcion: dia.descripcion, tipo: dia.tipo });
          }
          return (
            <>
              <CalendarioAnual
                anio={anio}
                dias={mapa}
                seleccionado={seleccionado}
                onClickDia={puedeEditar ? setSeleccionado : (f) => (mapa.has(f) ? setSeleccionado(f) : undefined)}
              />
              {seleccionado !== null && (
                <PanelDiaPlantilla
                  idPlantilla={idPlantilla}
                  fecha={seleccionado}
                  existente={d.dias.find((x) => x.fecha === seleccionado) ?? null}
                  puedeEditar={puedeEditar}
                  onCerrar={() => setSeleccionado(null)}
                  onCambio={() => {
                    setSeleccionado(null);
                    dias.recargar();
                    onCambio();
                  }}
                />
              )}
            </>
          );
        }}
      </Panel>
    </div>
  );
}

function PanelDiaPlantilla({
  idPlantilla,
  fecha,
  existente,
  puedeEditar,
  onCerrar,
  onCambio,
}: {
  idPlantilla: string;
  fecha: string;
  existente: DiaPlantilla | null;
  puedeEditar: boolean;
  onCerrar: () => void;
  onCambio: () => void;
}): ReactNode {
  const [enviando, setEnviando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState<'NO_OPERATIVO' | 'OPERATIVO_EXTRAORDINARIO'>('NO_OPERATIVO');
  const [error, setError] = useState<unknown>(null);

  if (existente !== null) {
    async function borrar(): Promise<void> {
      setEnviando(true);
      setError(null);
      try {
        await api.del(`/api/plantillas-calendario/${idPlantilla}/dias/${existente!.id}`);
        onCambio();
      } catch (err) {
        setError(err);
        setEnviando(false);
      }
    }
    return (
      <div className="calendario-detalle">
        <div className="fecha-detalle">{fecha}</div>
        <div className="suave pequena">{existente.descripcion}</div>
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila" style={{ marginTop: 10 }}>
          {!puedeEditar ? null : confirmando ? (
            <>
              <button type="button" className="pequeno" onClick={() => { void borrar(); }} disabled={enviando}>
                {enviando ? 'Quitando…' : 'Confirmar'}
              </button>
              <button type="button" className="pequeno" onClick={() => setConfirmando(false)} disabled={enviando}>
                Cancelar
              </button>
            </>
          ) : (
            <button type="button" className="pequeno" onClick={() => setConfirmando(true)}>
              Quitar de la plantilla
            </button>
          )}
          <button type="button" className="pequeno enlace" onClick={onCerrar} style={{ marginLeft: 'auto' }}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  if (!puedeEditar) return null;

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post<{ id: string }>(`/api/plantillas-calendario/${idPlantilla}/dias`, {
        fecha,
        tipo,
        descripcion: descripcion.trim() || (tipo === 'NO_OPERATIVO' ? 'Festivo' : 'Día operativo extraordinario'),
      });
      onCambio();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="calendario-detalle" onSubmit={(e) => { void enviar(e); }}>
      <div className="fecha-detalle">{fecha}</div>
      <p className="pequena tenue">Este día todavía no está en la plantilla.</p>
      <div className="fila" style={{ marginTop: 8, marginBottom: 8 }}>
        <label className="fila" style={{ gap: 4, fontWeight: 'normal' }}>
          <input type="radio" name="tipo-dia-plantilla" checked={tipo === 'NO_OPERATIVO'} onChange={() => setTipo('NO_OPERATIVO')} disabled={enviando} />
          Festivo
        </label>
        <label className="fila" style={{ gap: 4, fontWeight: 'normal' }}>
          <input
            type="radio"
            name="tipo-dia-plantilla"
            checked={tipo === 'OPERATIVO_EXTRAORDINARIO'}
            onChange={() => setTipo('OPERATIVO_EXTRAORDINARIO')}
            disabled={enviando}
          />
          Operativo extraordinario
        </label>
      </div>
      <div className="campo" style={{ marginBottom: 8 }}>
        <label htmlFor="descripcion-dia-plantilla">Descripción (opcional)</label>
        <input
          id="descripcion-dia-plantilla"
          type="text"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          disabled={enviando}
          placeholder={tipo === 'NO_OPERATIVO' ? 'Festivo' : 'Día operativo extraordinario'}
        />
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila">
        <button type="submit" className="principal pequeno" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Añadir a la plantilla'}
        </button>
        <button type="button" className="pequeno" onClick={onCerrar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
